/* eslint-disable max-len */
import React, { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { DateRange } from "@mui/x-date-pickers-pro/DateRangePicker";
import Box from "@mui/material/Box";
import { useListContext, useTranslate } from "react-admin";
import { CustomButton } from "./CustomButton";

const viewButtonStyle =
  "h-[32px] flex justify-start px-[16px] border-none items-center bg-mono-primaryLightBgColor font-['Inter'] font-[400] text-[14px] text-mono-primaryTextColor";
const activeViewButtonStyle =
  "h-[32px] flex justify-start px-[16px] border-none items-center bg-[#F8D1C0] font-['Inter'] font-[400] text-[14px] text-mono-black";

interface AppointmentDatesSelectProps {
  dateValue: DateRange<Dayjs>;
  setDateValue: React.Dispatch<React.SetStateAction<DateRange<Dayjs>>>;
  handleDatePopoverClose: () => void;
  fromOrderList?: boolean;
}

const AppointmentDatesSelect = ({
  dateValue,
  setDateValue,
  handleDatePopoverClose,
  fromOrderList
}: AppointmentDatesSelectProps) => {
  const { setFilters, filterValues } = useListContext();
  const translate = useTranslate();
  const [value, setValue] = useState<DateRange<Dayjs>>(dateValue);
  const today = dayjs();
  const prevWeek = today.subtract(7, "day");
  const nextWeek = today.add(7, "day");
  const startOfNextMonth = today.endOf("month").add(1, "day");

  return (
    <div className="text-[22px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor bg-mono-primaryLightBgColor">
      <div className="flex overflow-auto">
        <Box
          sx={{
            "& .MuiPickerStaticWrapper-content": {
              backgroundColor: "#FAF9F7"
            },
            "& .Mui-selected": {
              backgroundColor: "#F8D1C0 !important",
              color: "#000000"
            },
            "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": {
              backgroundColor: "rgba(248, 209, 192, 0.2)"
            },
            "& .MuiDateRangePickerDay-hiddenDayFiller": {
              backgroundColor: "transparent"
            }
          }}
        >
          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDateRangePicker
              displayStaticWrapperAs="desktop"
              value={value}
              onChange={(newValue) => {
                setValue(newValue);
              }}
            />
          </LocalizationProvider> */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDateRangePicker
              displayStaticWrapperAs="desktop"
              value={value}
              onChange={(newValue) => {
                setValue(newValue);
              }}
              renderInput={(startProps, endProps) => (
                <React.Fragment>
                  <TextField {...startProps} />
                  <Box sx={{ mx: 2 }}> to </Box>
                  <TextField {...endProps} />
                </React.Fragment>
              )}
            />
          </LocalizationProvider>
        </Box>
        <div className="flex flex-col border-l border-[rgba(48,47,46,0.1)] min-w-[114px]">
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") === today?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") === today?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([today?.startOf("day"), today?.endOf("day")]);
            }}
          >
            {translate("common.today")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                prevWeek?.startOf("week")?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                prevWeek?.endOf("week")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([prevWeek?.startOf("week"), prevWeek?.endOf("week")]);
            }}
          >
            {translate("common.last_week")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                today?.startOf("week")?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                today?.endOf("week")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([today.startOf("week"), today.endOf("week")]);
            }}
          >
            {translate("common.this_week")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                nextWeek?.startOf("week")?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                nextWeek?.endOf("week")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([nextWeek?.startOf("week"), nextWeek?.endOf("week")]);
            }}
          >
            {translate("common.next_week")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                today?.subtract(7, "day")?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") === today?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([today?.subtract(7, "day"), today]);
            }}
          >
            {translate("common.last_7_days")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") === today?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                today?.add(7, "day")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([today, today?.add(7, "day")]);
            }}
          >
            {translate("common.next_7_days")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                today?.startOf("month")?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                today?.endOf("month")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([today?.startOf("month"), today?.endOf("month")]);
            }}
          >
            {translate("common.this_month")}
          </button>
          <button
            className={
              value?.[0]?.format("YYYY-MM-D") ===
                startOfNextMonth?.format("YYYY-MM-D") &&
              value?.[1]?.format("YYYY-MM-D") ===
                startOfNextMonth?.endOf("month")?.format("YYYY-MM-D")
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => {
              setValue([startOfNextMonth, startOfNextMonth?.endOf("month")]);
            }}
          >
            {translate("common.next_month")}
          </button>
        </div>
      </div>
      <div className="flex w-full justify-between items-center gap-[16px] border-t border-[rgba(48,47,46,0.1)] p-[12px]">
        <CustomButton
          text={translate("common.clear_filters")}
          onClick={() => {
            setValue([dayjs(), dayjs().add(7, "day")]);
            setDateValue([dayjs(), dayjs().add(7, "day")]);
            fromOrderList
              ? setFilters(
                  {
                    ...filterValues,
                    created_at: {
                      gte: dayjs()?.startOf("day")?.toISOString(),
                      lte: dayjs().add(7, "day")?.endOf("day")?.toISOString()
                    }
                  },
                  undefined
                )
              : setFilters(
                  {
                    ...filterValues,
                    start_time: dayjs()?.startOf("day")?.toISOString(),
                    end_time: dayjs().add(7, "day")?.endOf("day")?.toISOString()
                  },
                  undefined
                );
            handleDatePopoverClose();
          }}
        />
        <div className="flex items-center gap-[8px]">
          <CustomButton
            text={translate("common.cancel")}
            variant="tertiary"
            onClick={() => {
              handleDatePopoverClose();
            }}
          />
          <CustomButton
            text={translate("common.done")}
            onClick={() => {
              setDateValue(value);
              fromOrderList
                ? setFilters(
                    {
                      ...filterValues,
                      created_at: {
                        gte: value?.[0]?.startOf("day")?.toISOString(),
                        lte: value?.[1]?.endOf("day")?.toISOString()
                      }
                    },
                    undefined
                  )
                : setFilters(
                    {
                      ...filterValues,
                      start_time: value?.[0]?.startOf("day")?.toISOString(),
                      end_time: value?.[1]?.endOf("day")?.toISOString()
                    },
                    undefined
                  );
              handleDatePopoverClose();
            }}
            disabled={!value?.[0] || !value?.[1]}
          />
        </div>
      </div>
    </div>
  );
};

export default AppointmentDatesSelect;
